import React, { CSSProperties } from 'react';
import { useField } from 'formik';
import { useDropzone } from 'react-dropzone';

import { Utils } from 'utils';
import { useToast } from 'hooks';
import { FirebaseImage, FormikField } from 'types';

import { Icon } from '../icon';
import { HorizontalImagesList } from '../images-preview';

import { ACCEPT } from './config';
import { CropperDialog } from './cropper';
import { MainContainer, DropzoneContainer } from './styles';

// eslint-disable-next-line import/no-extraneous-dependencies
import 'cropperjs/dist/cropper.css';

interface Props {
  error?: string;
  images: FirebaseImage[];
  onDrop: (image: FirebaseImage) => void;
  onRemove: (index: number) => void;
  maxImages?: number;
  onChangeOrder: (images: FirebaseImage[]) => void;
  dropzoneContainerStyle?: CSSProperties;
}

export const Dropzone = ({
  error,
  images,
  onDrop,
  onRemove,
  maxImages,
  onChangeOrder,
  dropzoneContainerStyle,
}: Props) => {
  const { showError } = useToast();

  const [isCropOpen, setIsCropOpen] = React.useState(false);
  const [fileToCrop, setFileToCrop] = React.useState<File | undefined>(
    undefined
  );

  const handleDrop = (files: File[]) => {
    setIsCropOpen(true);
    setFileToCrop(files[0]);
  };

  const dropzone = useDropzone({
    multiple: false,
    onDrop: handleDrop,
    accept: ACCEPT.Images,
    maxSize: Utils.Helpers.megabytesToBytes(15),
    onDropRejected: () => showError('Try another file'),
  });

  const cropUri = React.useMemo(() => {
    return fileToCrop ? URL.createObjectURL(fileToCrop) : undefined;
  }, [fileToCrop]);

  const previewImageURI = React.useMemo(() => {
    if (maxImages === 1 && images.length > 0) {
      const image = images[0];
      return image.file ? URL.createObjectURL(images[0].file!) : image.original;
    }
    return undefined;
  }, [images, maxImages]);

  const handleCropperClose = () => {
    setIsCropOpen(false);
    setFileToCrop(undefined);
  };

  const handleCrop = React.useCallback(
    (croppedFile: FirebaseImage) => {
      setIsCropOpen(false);
      setFileToCrop(undefined);
      onDrop(croppedFile);
    },
    [onDrop]
  );

  return (
    <MainContainer>
      <DropzoneContainer
        error={error}
        {...dropzone.getRootProps()}
        style={dropzoneContainerStyle}
      >
        <input {...dropzone.getInputProps()} />
        <div className="overlay-container">
          <Icon icon="camera" />
          <div className="labels-container">
            <p>Drag & Drop image in this area or</p>
            <h3>Click Here to attach</h3>
          </div>
        </div>

        {maxImages === 1 && previewImageURI && (
          <div className="image-preview-container">
            <img src={previewImageURI} />
          </div>
        )}
      </DropzoneContainer>

      {maxImages !== 1 && (
        <HorizontalImagesList
          images={images}
          onRemove={onRemove}
          onChange={onChangeOrder}
        />
      )}

      <CropperDialog
        uri={cropUri}
        open={isCropOpen}
        onCrop={handleCrop}
        onClose={handleCropperClose}
      />
    </MainContainer>
  );
};

export const DropzoneField = ({
  name,
  maxImages,
  ...props
}: FormikField<Partial<Props>>) => {
  const [field, meta, helpers] = useField<FirebaseImage[]>(name);

  const images = React.useMemo(() => {
    return field.value;
  }, [field.value]);

  const handleDrop = React.useCallback(
    (data: FirebaseImage) => {
      helpers.setValue(maxImages === 1 ? [data] : [...field.value, data]);
    },
    [maxImages, helpers, field.value]
  );

  return (
    <Dropzone
      {...props}
      images={images}
      error={meta.error}
      onDrop={handleDrop}
      maxImages={maxImages}
      onChangeOrder={helpers.setValue}
      onRemove={(index) =>
        helpers.setValue(images.filter((_, i) => i !== index))
      }
    />
  );
};
